import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div className="border-t border-ink-200 p-3 bg-white">
        <div className="flex gap-2 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            aria-label={t('chatbot.placeholder')}
            className={cn(
              'flex-1 px-3.5 py-2 text-[13.5px]',
              'bg-ink-100 border border-ink-200 rounded-sm',
              'text-ink-800 placeholder:text-ink-500',
              'focus:outline-none focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-200',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !input.trim()}
            className={cn(
              'p-2.5 rounded-sm flex-shrink-0',
              'bg-brand-500 text-white',
              'hover:bg-brand-600 transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
              'disabled:bg-ink-300 disabled:cursor-not-allowed'
            )}
            aria-label={t('chatbot.send')}
          >
            <Send className="w-4 h-4" strokeWidth={2.25} />
          </button>
        </div>
      </div>
    )
  }
)
